import type { CreatePortcallFormValues } from '../../../../composables/useCreatePortcallForm'

/** The ordered groups the steps are bucketed into in the sidebar. */
export type StepGroup = 'Who' | 'What' | 'Where' | 'When' | 'Review'

/** Ordered group headings for the sidebar. */
export const PORTCALL_STEP_GROUPS: readonly StepGroup[] = [
  'Who',
  'What',
  'Where',
  'When',
  'Review'
]

/**
 * A `list` step searches a reference list; an `arrival` step edits an ETA; the
 * `summary` step recaps every answer and gates the create action.
 */
export type StepKind = 'list' | 'arrival' | 'summary'

/** One option row in a list step. */
export interface StepOption {
  /** The value stored on the form field. */
  value: string
  /** Primary label. */
  label: string
  /** Secondary muted line (e.g. a code or location). */
  sub?: string
  /**
   * Optional 2-letter country code. When set, the row renders a `FlagIcon`
   * instead of the step's generic icon (e.g. a vessel's flag state).
   */
  flag?: string
}

/** Form fields a list step can target (string-valued). */
export type ListStepKey = Extract<
  keyof CreatePortcallFormValues,
  | 'agencyId'
  | 'appointingPrincipal'
  | 'vesselImo'
  | 'callType'
  | 'portOfCallLocationCode'
>

/** Form fields an arrival step can target (ETA-valued). */
export type ArrivalStepKey = Extract<
  keyof CreatePortcallFormValues,
  'etaAtPilotStation' | 'etaAtPort'
>

/** The synthetic key of the final review/create step (not a form field). */
export type SummaryStepKey = 'summary'

/**
 * A single step in the create-portcall wizard. This is the lean
 * sequence-of-truth the wizard model and sidebar read; per-step presentation
 * (the heading question, search placeholder, search-first behaviour, the TBN
 * affordance) lives in each step's own component.
 */
export interface PortcallStep {
  /** The form field this step edits, or `summary` for the final review step. */
  key: ListStepKey | ArrivalStepKey | SummaryStepKey
  group: StepGroup
  /** Short label shown in the sidebar. */
  label: string
  kind: StepKind
  /** Arrival steps that may be skipped (the pilot-station arrival). */
  optional?: boolean
}

/**
 * The wizard's step sequence, grouped Who → What → Where → When. Mirrors the
 * fields of the classic form; the pilot-station arrival is only shown for
 * ports that capture one (see the wizard's visibility logic).
 */
export const PORTCALL_STEPS: readonly PortcallStep[] = [
  { key: 'agencyId', group: 'Who', label: 'Agency', kind: 'list' },
  {
    key: 'appointingPrincipal',
    group: 'Who',
    label: 'Nominator / Principal',
    kind: 'list'
  },
  { key: 'vesselImo', group: 'What', label: 'Vessel', kind: 'list' },
  { key: 'callType', group: 'What', label: 'Port call type', kind: 'list' },
  {
    key: 'portOfCallLocationCode',
    group: 'Where',
    label: 'Port of call',
    kind: 'list'
  },
  {
    key: 'etaAtPilotStation',
    group: 'When',
    label: 'Arrival Pilot Station',
    kind: 'arrival',
    optional: true
  },
  { key: 'etaAtPort', group: 'When', label: 'Arrival Port', kind: 'arrival' },
  { key: 'summary', group: 'Review', label: 'Review & create', kind: 'summary' }
]
